import { Css, ScrollableParent, Step, useComputed } from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useEffect, useMemo, useState } from "react";
import { StepperProvider, stepperBarHeight, useStepperContext } from "src/components/stepper";
import { BidScopeSource, Maybe, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  AddBidPackagePageQuery,
  AddBidPackagePage_BidPackagesFragment,
  AddBidPackagePage_OriginatingProjectFragment,
  useAddBidPackagePageQuery,
  useAddBidPackagePage_OriginatingProjectQuery,
} from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { INTERNAL_ESTIMATE_ID } from "src/routes/bids/plan-based-bid-package/utils";
import { BidPackageParams } from "src/routes/routesDef";
import { isDefined, isEmptyObject, queryResult, zeroTo } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { BidPackageDetailsStep } from "./components/1.BidPackageDetailsStep";
import { ReviewUnitBidScopeStep } from "./components/2.ReviewUnitBidScopeStep";
import { SelectBiddersStep } from "./components/3.SelectBiddersStep";
import { ReviewAndSendStep } from "./components/5.ReviewAndSendStep";
import { BidPackageStepActions } from "./components/BidPackageStepActions";
import { BidPackageStep } from "./utils";

export function AddBidPackagePage() {
  const { bidPackageGroupId } = useRouteParams<BidPackageParams>();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const query = useAddBidPackagePageQuery({ variables: { id: bidPackageGroupId || "" }, skip: !bidPackageGroupId });

  const originatingProjectQuery = useAddBidPackagePage_OriginatingProjectQuery({
    variables: { id: projectId! },
    skip: !!bidPackageGroupId || !projectId,
  });

  if (!bidPackageGroupId) {
    return projectId ? (
      queryResult(originatingProjectQuery, ({ project }) => (
        <StepperProvider steps={initialSteps(true)}>
          <AddBidPackageView originatingProject={project} />
        </StepperProvider>
      ))
    ) : (
      <StepperProvider steps={initialSteps(true)}>
        <AddBidPackageView />
      </StepperProvider>
    );
  }

  // Don't disable steps in `edit` mode on stepper initialization
  // Our formState useComputed validation will still check required fields if edits are made per step
  return queryResult(query, ({ bidPackageGroup }) => (
    <StepperProvider steps={initialSteps(!bidPackageGroup)}>
      <AddBidPackageView bidPackageGroup={bidPackageGroup} />
    </StepperProvider>
  ));
}

type AddBidPackageViewProps = {
  bidPackageGroup?: AddBidPackagePageQuery["bidPackageGroup"];
  originatingProject?: AddBidPackagePage_OriginatingProjectFragment;
};

function AddBidPackageView({ bidPackageGroup, originatingProject }: AddBidPackageViewProps) {
  const { bidPackageGroupId: savedGroupId } = useRouteParams<BidPackageParams>();
  const { currentStep, setSteps } = useStepperContext();
  const [stepIndex, setStepIndex] = useState(0);
  const formState = useFormState({
    config: formConfig,
    init: {
      input: bidPackageGroup,
      map: (bidPackageGroup) => mapBidPackagesToForm(bidPackageGroup.bidPackages),
      ifUndefined: {
        marketId: originatingProject?.market?.id,
        scopeSource: BidScopeSource.ProductOffering,
        bidPackages: [{ delete: undefined }],
      },
    },
  });

  // This updates the continue/enable/disable state of the stepper based on required formstate
  useComputed(() => {
    if (formState.dirty && !isEmptyObject(formState.changedValue)) {
      const coreFieldsEmpty =
        !formState.marketId.value ||
        formState.bidPackages.value?.isEmpty ||
        !formState.costType.valid ||
        !formState.scopeSource.valid ||
        ([BidScopeSource.Lot, BidScopeSource.Subdivision, BidScopeSource.Community].includes(
          formState.scopeSource.value!,
        ) &&
          !formState.projects.value?.length);

      setSteps((prevState) => [
        { ...prevState[0], disabled: false },
        { ...prevState[1], disabled: coreFieldsEmpty },
        { ...prevState[2], disabled: coreFieldsEmpty },
        { ...prevState[3], disabled: coreFieldsEmpty },
      ]);
    }
  }, [formState]);

  useEffect(() => {
    const currentStepIndex = initialSteps(!isDefined(savedGroupId)).findIndex(
      (step) => step.value === currentStep.value,
    );
    if (stepIndex === currentStepIndex) return;
    setStepIndex(currentStepIndex);
    setSteps((prevState) =>
      zeroTo(4, (i) => ({ ...prevState[i], state: currentStepIndex > [i, 3].min() ? "complete" : prevState[i].state })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const step = useMemo(() => {
    switch (currentStep.value) {
      case BidPackageStep.PACKAGE_DETAILS:
        return <BidPackageDetailsStep formState={formState} originatingProject={originatingProject} />;
      case BidPackageStep.REVIEW_SCOPE:
        return <ReviewUnitBidScopeStep formState={formState} />;
      case BidPackageStep.SELECT_BIDDERS:
        return <SelectBiddersStep formState={formState} />;
      case BidPackageStep.REVIEW_AND_DOWNLOAD:
        return <ReviewAndSendStep formState={formState} />;
      default:
        return <BidPackageDetailsStep formState={formState} />;
    }
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep.value, formState]);

  return (
    <ScrollableParent px={8} xss={Css.mbPx(stepperBarHeight).$}>
      {step}
      <BidPackageStepActions formState={formState} />
    </ScrollableParent>
  );
}

const INCOMPLETE = "incomplete" as const;
export const initialSteps: (isCreateMode: boolean) => Step[] = (isCreateMode = true) => [
  { value: BidPackageStep.PACKAGE_DETAILS, label: "Package Details", state: INCOMPLETE, disabled: isCreateMode },
  { value: BidPackageStep.REVIEW_SCOPE, label: "Review Scope", state: INCOMPLETE, disabled: isCreateMode },
  { value: BidPackageStep.SELECT_BIDDERS, label: "Select Bidders", state: INCOMPLETE, disabled: isCreateMode },
  {
    value: BidPackageStep.REVIEW_AND_DOWNLOAD,
    label: "Review and Download",
    state: INCOMPLETE,
    disabled: isCreateMode,
  },
];

export type BidPackageFormState = Pick<
  SaveBidPackageInput,
  "bidPackageGroupId" | "costType" | "projects" | "scopeSource"
> & { marketId?: Maybe<string>; bidPackages: Pick<SaveBidPackageInput, "requests" | "id" | "costCodes" | "delete">[] };

export const formConfig: ObjectConfig<BidPackageFormState> = {
  marketId: { type: "value", rules: [required] },
  projects: { type: "value" },
  bidPackageGroupId: { type: "value" },
  costType: { type: "value", rules: [required] },
  scopeSource: { type: "value", rules: [required] },
  bidPackages: {
    type: "list",
    config: {
      id: { type: "value" },
      costCodes: { type: "value", rules: [required] },
      delete: { type: "value" },
      requests: {
        type: "list",
        config: {
          id: { type: "value" },
          tradePartnerId: { type: "value" },
          archive: { type: "value" },
          primaryContactId: { type: "value", rules: [] },
          secondaryContactId: { type: "value" },
        },
      },
    },
    rules: [required],
  },
};

export function mapBidPackagesToForm(bidPackages: AddBidPackagePage_BidPackagesFragment[]) {
  const {
    bidPackageGroup,
    // Deconstruct to reformat for formState
    projects,
    costType,
    scopeSource,
  } = bidPackages?.first ?? {};
  return {
    bidPackageGroupId: bidPackageGroup?.id,
    ...bidPackages?.first,
    costType: costType?.code,
    scopeSource: scopeSource?.code,
    // Even if the bid package is for specific projects we still want to render the market selector
    marketId: projects?.first?.market?.id,
    projects: projects?.map((p) => p.id),
    bidPackages: (bidPackages ?? []).map((bp) => ({
      id: bp.id,
      costCodes: bp.costCodes.map((cc) => cc.id),
      delete: false,
      requests: [
        ...(bp.internalEstimateBidContract
          ? [
              {
                id: bp.internalEstimateBidContract.id,
                tradePartnerId: INTERNAL_ESTIMATE_ID,
                archive: !!bp.internalEstimateBidContract.archivedAt,
              },
            ]
          : []),
        ...bp.requests.map((r) => ({
          id: r.id,
          tradePartnerId: r.tradePartner.id,
          primaryContactId: r.primaryContact?.id,
          secondaryContactId: r.secondaryContact?.id,
        })),
      ],
    })),
  };
}
